import { useEffect, useState, MutableRefObject } from 'react'

import useIsServer from 'hooks/useIsServer'

type HlsInstance = {
  loadSource: (src: string) => void
  attachMedia: (el: HTMLVideoElement) => void
  destroy: () => void
}

// hls.js is ~150KB minified. Browsers with native HLS support (Safari,
// iOS) never need it, so dynamic import skips the cost entirely there.
// On other browsers we only download it at first play, not at render.
const loadVideo = async (
  video: HTMLVideoElement,
  videoSrc: string
): Promise<HlsInstance | undefined> => {
  if (video.canPlayType('application/vnd.apple.mpegurl')) {
    // eslint-disable-next-line no-param-reassign
    video.src = videoSrc
    return undefined
  }

  const { default: Hls } = await import(/* webpackChunkName: "hls" */ 'hls.js')

  if (!Hls?.isSupported()) {
    // eslint-disable-next-line no-console
    console.error("This is a legacy browser that doesn't support MSE")
    return undefined
  }

  const hls = new Hls({
    enableWorker: true,
    lowLatencyMode: true,
    capLevelToPlayerSize: true,
    backBufferLength: 90,
  })
  hls.loadSource(videoSrc)
  hls.attachMedia(video)
  return hls
}

export const useVideoPlaying = (
  videoRef: MutableRefObject<HTMLVideoElement>
) => {
  const [videoPlaying, setVideoPlaying] = useState(false)

  useEffect(() => {
    videoRef.current?.addEventListener('play', () => setVideoPlaying(true))
    videoRef.current?.addEventListener('pause', () => setVideoPlaying(false))

    return () => {
      videoRef.current?.removeEventListener('play', () => setVideoPlaying(true))
      videoRef.current?.removeEventListener('pause', () =>
        setVideoPlaying(false)
      )
    }
  }, [videoRef])

  return videoPlaying
}

export const useForcePause = (
  videoRef: MutableRefObject<HTMLVideoElement>,
  forcePause: boolean
) => {
  useEffect(() => {
    if (videoRef.current != null && forcePause === true) {
      videoRef.current.pause()
    }
  }, [forcePause, videoRef])
}

export const useLoadVideo = (
  videoRef: MutableRefObject<HTMLVideoElement>,
  src: string
) => {
  const isServer = useIsServer()
  useEffect(() => {
    // loadVideo is now async because hls.js loads on demand. Track the
    // resolved instance and a `cancelled` flag so the cleanup destroys
    // the right hls instance even if the effect re-runs before the
    // promise settles.
    let hls: HlsInstance | undefined
    let cancelled = false
    if (videoRef.current && src) {
      loadVideo(videoRef.current, src).then((instance) => {
        if (cancelled) {
          instance?.destroy()
        } else {
          hls = instance
        }
      })
    }

    return () => {
      cancelled = true
      if (hls) {
        hls.destroy()
      }
    }
  }, [videoRef, src, isServer])
}
